import { motion } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';
import { GraduationCap, Scale } from 'lucide-react';

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.15 } } };

const LAWYER_PORTRAIT = "https://customer-assets.emergentagent.com/job_ozturk-law-pro/artifacts/nepfc6nk_WhatsApp%20Image%202026-07-02%20at%2015.08.39.jpeg";

export default function AboutPage() {
  const { t, language } = useLanguage();

  const expertise = language === 'tr'
    ? ["Ceza Hukuku", "Aile Hukuku", "İş Hukuku", "Ticaret Hukuku", "İcra Hukuku", "Gayrimenkul Hukuku"]
    : ["Criminal Law", "Family Law", "Labor Law", "Commercial Law", "Enforcement Law", "Real Estate Law"];

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="about-page">
      <section className="py-24 bg-[#121212]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-[#C5A059] text-xs uppercase tracking-[0.3em] font-medium">{t('about.title')}</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#F3F4F6] mt-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{t('about.subtitle')}</h1>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
              <img src={LAWYER_PORTRAIT} alt="Av. Samet Öztürk" className="w-full rounded-sm object-cover aspect-[3/4]" />
            </motion.div>

            <motion.div initial="hidden" animate="visible" variants={stagger}>
              <motion.p variants={fadeUp} className="text-[#A1A1AA] leading-relaxed text-base mb-4">
                {language === 'tr'
                  ? 'Dokuz Eylül Üniversitesi Hukuk Fakültesi mezunu olan Av. Samet Öztürk, yaklaşık 3 yıllık mesleki deneyimi ile müvekkillerine güvenilir, çözüm odaklı ve profesyonel hukuki danışmanlık hizmeti sunmaktadır.'
                  : 'Attorney Samet Öztürk, a graduate of Dokuz Eylül University Faculty of Law, provides reliable, solution-oriented and professional legal consultancy services with approximately 3 years of professional experience.'}
              </motion.p>
              <motion.p variants={fadeUp} className="text-[#A1A1AA] leading-relaxed text-base mb-4">
                {language === 'tr'
                  ? 'Hukukun üstünlüğü, etik değerler ve müvekkil memnuniyeti ilkelerini esas alan Av. Öztürk; her dosyayı titizlikle değerlendirerek, müvekkillerinin hak ve menfaatlerini en etkin şekilde korumayı amaçlamaktadır.'
                  : 'Based on the principles of the rule of law, ethical values and client satisfaction, Atty. Öztürk aims to protect the rights and interests of his clients by meticulously evaluating each case.'}
              </motion.p>
              <motion.p variants={fadeUp} className="text-[#A1A1AA] leading-relaxed text-base mb-8">
                {language === 'tr'
                  ? 'Güncel mevzuatı yakından takip eden ve her hukuki sürece stratejik bir bakış açısıyla yaklaşan Av. Öztürk, şeffaf iletişim anlayışıyla müvekkillerini sürecin her aşamasında bilgilendirmektedir. Başta ceza hukuku, aile hukuku, iş hukuku ve ticaret hukuku olmak üzere çeşitli hukuk alanlarında hizmet vermekte; bireysel ve kurumsal müvekkillerine etkili, güvenilir ve sonuç odaklı hukuki çözümler sunmaktadır.'
                  : 'Closely following current legislation and approaching each legal process with a strategic perspective, Atty. Öztürk informs his clients at every stage of the process with a transparent communication approach.'}
              </motion.p>

              <motion.div variants={fadeUp} className="p-6 bg-[#121212] border border-white/10 rounded-sm mb-6">
                <div className="flex items-center gap-3 mb-3">
                  <GraduationCap className="h-5 w-5 text-[#C5A059]" />
                  <h3 className="text-lg font-semibold text-[#F3F4F6]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{t('about.education')}</h3>
                </div>
                <p className="text-[#A1A1AA] text-sm">{t('about.education_detail')}</p>
              </motion.div>

              <motion.div variants={fadeUp} className="p-6 bg-[#121212] border border-white/10 rounded-sm">
                <div className="flex items-center gap-3 mb-4">
                  <Scale className="h-5 w-5 text-[#C5A059]" />
                  <h3 className="text-lg font-semibold text-[#F3F4F6]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{t('about.expertise')}</h3>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {expertise.map((item, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-[#C5A059] rounded-full" />
                      <span className="text-[#A1A1AA] text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
